/** venture/fundbody.ts — 자금운용현황(별지 제9호) 생성 요청 본문 조립 (순수 함수, 2026-09-24)
 *  자동: 출자액(결성연도에 전액) · 전환사채(법인별 투자금액을 사채 발행일 연도로 전년/금년 분리) · 미투자자산(수익−지출)
 *  입력(s15 txt, 원 단위): 수익 4종·지출 8종의 전년말/금년도, 수익 내역, 미투자자산 운용내역, 법인별 납입자본금
 *  설립일·벤처확인번호는 13단계 VICS 1313 입력값을 그대로 쓴다 */
import { getTxt } from './stepcommon';
import { bondVal } from './bondcalc';
import { regCoOf, won } from './coterms';
import { regDateOf } from './report';
import { vicsSlot } from './vics13';
import type { FundStatusBody, FundPair, FundCompanyRow } from './api';
import type { Project, CoCard } from './types';

export const FS_IN: [string, string][] = [['stkg', '주식 양도차익'], ['div', '배당 수익'], ['fin', '금융상품 수익 (이자 등)'], ['etc', '기타 수익']];
export const FS_OUT: [string, string][] = [['stk', '주식 인수 (투자)'], ['bw', '신주인수권부사채 (투자)'], ['safe', '조건부지분인수계약 (투자)'],
  ['dist', '배분'], ['mgmt', '관리보수'], ['perf', '성과보수'], ['exp', '기타경비'], ['loss', '투자손실']];
export const FS_AUTO: [string, string][] = [['chul', '출자액'], ['cb', '전환사채 (투자)']];

export const fsKey = (y: number, k: string): string => 'fs_' + y + '_' + k;
export const fsTxt = (p: Project, y: number, k: string): string => getTxt(p, 's15', fsKey(y, k));
const fsNum = (p: Project, y: number, k: string): number => won(fsTxt(p, y, k));
const yearOf = (iso: string): number => parseInt((iso || '').slice(0, 4), 10) || 0;

export interface FundAuto { chul: FundPair; cb: FundPair; rows: FundCompanyRow[]; }
/** 자동 계산분 — 출자액·전환사채·법인 행 */
export function fundAuto(p: Project, y: number): FundAuto {
  const reg = regDateOf(p).date; const ry = yearOf(reg); const total = won(p.total);
  const chul: FundPair = [ry && ry < y ? total : 0, ry === y || (!ry && total) ? total : 0];
  const cb: FundPair = [0, 0];
  const cos: CoCard[] = (p.collect && p.collect.cos) || [];
  const rows: FundCompanyRow[] = cos.map((co) => {
    const rc = regCoOf(p, co); const amt = won(rc ? rc.amt : '');
    const issue = getTxt(p, 's8', co.id + '_b_issueDate'); const iy = yearOf(issue);
    if (iy && iy < y) cb[0] += amt; else if (iy === y || !iy) cb[1] += amt;   // 발행일 없으면 금년도로
    const tm = co.companyId && p.tmco ? p.tmco[co.companyId] : undefined;   // 업체DB 스냅샷 (13·14단계에서 채워짐)
    return {
      name: co.name || '', tel: (tm && tm.ceo_phone) || (rc && rc.mgrTel) || '', ceo: (rc && rc.ceo) || co.rep || '', addr: bondVal(p, co, 'issuerAddr') || (tm && tm.address) || '',
      capital: fsNum(p, y, 'co_' + co.id + '_capital'),
      founded: getTxt(p, 's13', vicsSlot(co.id, 'founded')) || (tm && tm.established_date ? String(tm.established_date).slice(0, 10) : ''), vno: getTxt(p, 's13', vicsSlot(co.id, 'vno')),
      date: issue, stk: 0, cb: amt, bw: 0, safe: 0,
    };
  });
  return { chul, cb, rows };
}

/** 입력값이 있으면 입력값, 없으면 자동값 */
function pairOf(p: Project, y: number, k: string, auto?: FundPair): FundPair {
  const pv = fsTxt(p, y, k + '_prev'); const cv = fsTxt(p, y, k + '_cur');
  return [pv ? won(pv) : (auto ? auto[0] : 0), cv ? won(cv) : (auto ? auto[1] : 0)];
}

/** 미투자자산 누계 = 수익 합계 − 지출(투자·배분·보수·경비·손실) — 조합통장 잔액 기본값 */
export function uninvested(p: Project, y: number): number {
  const au = fundAuto(p, y);
  const sum = (pr: FundPair) => pr[0] + pr[1];
  let v = sum(pairOf(p, y, 'chul', au.chul));
  FS_IN.forEach(([k]) => { v += sum(pairOf(p, y, k)); });
  v -= sum(pairOf(p, y, 'cb', au.cb));
  FS_OUT.forEach(([k]) => { v -= sum(pairOf(p, y, k)); });
  return v;
}

export function fundBody(p: Project, y: number): FundStatusBody {
  const au = fundAuto(p, y);
  const a: Record<string, FundPair> = { chul: pairOf(p, y, 'chul', au.chul), cb: pairOf(p, y, 'cb', au.cb) };
  const notes: Record<string, string> = {};
  const b: FundStatusBody['b'] = {};
  FS_IN.concat(FS_OUT).forEach(([k]) => { a[k] = pairOf(p, y, k); notes[k] = fsTxt(p, y, k + '_n'); });
  FS_IN.forEach(([k]) => { b[k] = { d1: fsTxt(p, y, k + '_d1'), d2: fsTxt(p, y, k + '_d2'), n: fsTxt(p, y, k + '_n') }; });
  const genBal = fsTxt(p, y, 'gen_bal');
  const c: FundStatusBody['c'] = {
    gen: { name: fsTxt(p, y, 'gen_name') || '보통예금 (조합통장)', org: fsTxt(p, y, 'gen_org'), date: fsTxt(p, y, 'gen_date') || regDateOf(p).date, bal: genBal ? won(genBal) : uninvested(p, y), n: fsTxt(p, y, 'gen_n') },
    etc: { name: fsTxt(p, y, 'etc_name'), org: fsTxt(p, y, 'etc_org'), date: fsTxt(p, y, 'etc_date'), bal: fsNum(p, y, 'etc_bal'), n: fsTxt(p, y, 'etc_n') },
  };
  return { year: y, org: p.org, a, notes, b, c, d: au.rows };
}
